import {
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
export const Route = createFileRoute("/admin/_layout/crm/cases")({
  component: CasesPage,
});

import { useState, useEffect, useMemo } from "react";
import instance from "@/lib/axios";
import { getCrmUrl } from "@/lib/crm";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { CaseTable } from "@/components/crm/tables/CaseTable";
import { CaseFormSheet } from "@/components/crm/forms/CaseFormSheet";
import {
  useFetchCases,
  useCaseMutations,
  type CaseOrdering,
} from "@/hooks/api/crm/useCases";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import type { CrmCase, CrmCasePayload } from "@/types/crm";

function CasesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const viewId = new URLSearchParams(location.searchStr).get("view") ?? undefined;

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  // Undefined = all team/org records (admins see everyone by default); pick a user to narrow.
  const [assignedTo, setAssignedTo] = useState<number | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        subject: SortDirection;
        status: SortDirection;
        priority: SortDirection;
        assigned_to__first_name: SortDirection;
        created_at: SortDirection;
      },
      CaseOrdering
    >({
      subject: null,
      status: null,
      priority: null,
      assigned_to__first_name: null,
      created_at: null,
    });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<CrmCase | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState<CrmCase | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (!viewId) return;
    instance
      .get<CrmCase>(getCrmUrl(`cases/${viewId}/`))
      .then((res) => {
        setEditingCase(res.data);
        setIsViewOnly(true);
        setIsSheetOpen(true);
      })
      .catch(() =>
        navigate({ to: "/admin/crm/cases", search: {} })
      );
  }, [viewId]);

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    if (viewId)
      navigate({ to: "/admin/crm/cases", search: {} });
  };

  const casesQuery = useFetchCases({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    assigned_to: assignedTo ? String(assignedTo) : undefined,
    ordering: getApiOrdering(),
  });
  const cases = casesQuery.data?.results ?? [];
  const count = casesQuery.data?.count ?? 0;

  const { createCase, updateCase, deleteCase } = useCaseMutations();

  const isMutating =
    createCase.isPending || updateCase.isPending || deleteCase.isPending;
  const isLoading = casesQuery.isPending || isMutating;
  const isFetching = casesQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, priorityFilter, assignedTo, ordering, setPage]);

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setStatusFilter("");
    setPriorityFilter("");
    setAssignedTo(undefined);
    setOrdering({
      subject: null,
      status: null,
      priority: null,
      assigned_to__first_name: null,
      created_at: null,
    });
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const onCreate = async (data: CrmCasePayload) => {
    await createCase.mutateAsync(data);
    toast.success("Case opened!");
    setIsSheetOpen(false);
  };
  const onEdit = async (id: string, data: Partial<CrmCasePayload>) => {
    await updateCase.mutateAsync({ id, payload: data });
    toast.success("Case updated!");
    setIsSheetOpen(false);
  };
  const handleDeleteConfirm = async () => {
    if (!caseToDelete) return;
    try {
      await deleteCase.mutateAsync({ id: caseToDelete.id });
      toast.success("Case deleted!");
      setIsDeleteDialogOpen(false);
      setCaseToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Cases"
          description="Manage support cases."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => {
            setEditingCase(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Open Case"
          isLoading={isFetching}
        />
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search cases..."
          />
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {[
                "New",
                "Assigned",
                "In Progress",
                "Pending Input",
                "Resolved",
                "Rejected",
                "Closed",
              ].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={priorityFilter}
            onValueChange={(v) => setPriorityFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="All Priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              {["Low", "Normal", "High"].map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} />
        </FiltersPanel>
        <CaseTable
          cases={cases}
          onView={(c) =>
            navigate({
              to: "/admin/crm/cases/$id",
              params: { id: c.id },
            })
          }
          onViewAccount={(accountId) => {
            navigate({
              to: "/admin/crm/accounts/$id",
              params: { id: accountId },
            });
          }}
          onEdit={(c) => {
            setEditingCase(c);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(c) => {
            setCaseToDelete(c);
            setIsDeleteDialogOpen(true);
          }}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <CaseFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onClose={handleCloseSheet}
          onCreate={onCreate}
          onEdit={onEdit}
          editingCase={editingCase}
          isLoading={isMutating}
          onViewAccount={(accountId) => {
            navigate({
              to: "/admin/crm/accounts/$id",
              params: { id: accountId },
            });
          }}
        />
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Case"
          description={`Delete case "${caseToDelete?.subject}"?`}
          itemName={caseToDelete?.subject}
          isLoading={deleteCase.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
